import React, { useState, useEffect } from 'react';
import WhaleLogo from '../WhaleLogo';
import ThemeToggle from '../ThemeToggle';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUserProfile, isInvestor } from '../../services/userApi';
import Logo from '../Logo';

interface PublicLayoutProps {
  children: React.ReactNode;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isUserInvestor, setIsUserInvestor] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const checkUserType = async () => {
      if (!currentUser) {
        setIsUserInvestor(false);
        return;
      }

      try {
        const profile = await getUserProfile(currentUser.uid);
        setIsUserInvestor(isInvestor(profile.type));
      } catch {
        setIsUserInvestor(false);
      }
    };

    checkUserType();
  }, [currentUser]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header
        className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100"
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Logo size="lg" linkTo="/" />

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors ${
                  isActive('/') ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Home
              </Link>
              <Link
                to="/pricing"
                className={`text-sm font-medium transition-colors ${
                  isActive('/pricing') ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                className={`text-sm font-medium transition-colors ${
                  isActive('/contact') ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Contact
              </Link>
              <Link
                to="/become-investor"
                className={`text-sm font-medium transition-colors ${
                  isActive('/become-investor') ? 'text-purple-600' : 'text-purple-600 hover:text-purple-800'
                }`}
              >
                Become Partner
              </Link>
              <a
                href="https://kids.litrlly.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-pink-500 hover:text-pink-600 transition-colors flex items-center gap-1"
              >
                <span>🐳</span> For Kids
              </a>
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <ThemeToggle />
              {currentUser ? (
                <>
                  {isUserInvestor && (
                    <Link
                      to="/investor"
                      className="inline-flex items-center px-4 py-2 border border-purple-600 text-sm font-medium rounded-lg text-purple-600 hover:bg-purple-50 transition-colors"
                    >
                      Personal Cabinet
                    </Link>
                  )}
                  <Link
                    to="/app"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    Go to App
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-100 py-4">
              <div className="flex flex-col space-y-2">
                <div className="px-4 pb-2">
                  <ThemeToggle className="w-full justify-center py-3" alwaysShowLabel />
                </div>
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-4 text-lg font-medium rounded-lg transition-colors ${
                    isActive('/') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Home
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-4 text-lg font-medium rounded-lg transition-colors ${
                    isActive('/pricing') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Pricing
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-4 text-lg font-medium rounded-lg transition-colors ${
                    isActive('/contact') ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Contact
                </Link>
                <Link
                  to="/become-investor"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-4 text-lg font-medium rounded-lg transition-colors ${
                    isActive('/become-investor') ? 'text-purple-600 bg-purple-50' : 'text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  Become Partner
                </Link>
                <a
                  href="https://kids.litrlly.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-4 text-lg font-medium rounded-lg text-pink-500 hover:bg-pink-50 transition-colors flex items-center gap-2"
                >
                  <span>🐳</span> For Kids
                </a>

                <div className="border-t border-gray-100 pt-4 mt-2">
                  {currentUser ? (
                    <>
                      {isUserInvestor && (
                        <Link
                          to="/investor"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-4 text-lg font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        >
                          Personal Cabinet
                        </Link>
                      )}
                      <Link
                        to="/app"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block mt-2 px-4 py-4 text-lg font-medium text-center text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        Go to App
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-4 text-lg font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/login"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block mt-2 px-4 py-4 text-lg font-medium text-center text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        Get Started
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <WhaleLogo className="w-10 h-10" />
                <span className="text-xl font-bold text-white">Litrlly</span>
              </div>
              <p className="text-sm max-w-md">
                Improve your English pronunciation with AI-powered feedback. Practice speaking,
                get instant analysis, and track your progress.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                Quick Links
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-sm hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-sm hover:text-white transition-colors">
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm hover:text-white transition-colors">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/become-investor" className="text-sm hover:text-white transition-colors">
                    Become Partner
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kids.litrlly.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm hover:text-white transition-colors flex items-center gap-1"
                  >
                    <span>🐳</span> For Kids
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                Legal
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-sm hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Litrlly. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
